import React, { useCallback, useDeferredValue, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Avatar,
  Box,
  Button,
  Checkbox,
  Chip,
  Container,
  FormControlLabel,
  LinearProgress,
  List,
  ListItemButton,
  ListItemText,
  Paper,
  Stack,
  Switch,
  TextField,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import BusinessIcon from '@mui/icons-material/Business';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import SaveIcon from '@mui/icons-material/Save';
import { useNavigate } from 'react-router-dom';
import {
  createTenant,
  deleteTenant,
  fetchTenantById,
  fetchTenants,
  updateTenant,
} from '../api/tenantManagement';
import { pageColors } from './HomePage';

const PAGE_BACKGROUND =
  'radial-gradient(circle at top left, rgba(2,119,189,0.14), transparent 55%), #f5f7fb';

const SURFACE_SX = {
  borderRadius: 4,
  backgroundColor: '#fff',
  boxShadow: '0 18px 40px rgba(0,0,0,0.07)',
  border: '1px solid rgba(0,0,0,0.05)',
};

const TENANT_COLUMNS_SX = {
  xs: 'minmax(0, 1fr)',
  lg: 'minmax(300px, 4fr) minmax(0, 8fr)',
};

function createDraft() {
  return {
    company: '',
    clientId: '',
    clientSecret: '',
    crmEnabled: false,
  };
}

function editDraft(tenant) {
  return {
    company: tenant?.company || '',
    clientId: '',
    clientSecret: '',
    clearClientId: false,
    crmEnabled: Boolean(tenant?.crmEnabled),
  };
}

function formatTimestamp(value) {
  if (!value) {
    return 'Not available';
  }

  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? value : parsed.toLocaleString();
}

function isForbidden(error) {
  return error?.status === 403;
}

function orderTenants(tenants) {
  return [...tenants].sort((left, right) => left.company.localeCompare(right.company));
}

function formatDeletionConflict(error) {
  const dependencies = error?.data?.dependencies;

  if (error?.status !== 409 || !dependencies || typeof dependencies !== 'object') {
    return error?.message || 'Failed to delete the tenant.';
  }

  const dependencyLabels = {
    users: 'users',
    fortnoxTokens: 'Fortnox tokens',
    crmRows: 'CRM records',
    alerts: 'alerts',
    snapshots: 'snapshots',
  };
  const blockingDependencies = Object.entries(dependencyLabels)
    .map(([key, label]) => ({ label, count: Number(dependencies[key] || 0) }))
    .filter(({ count }) => count > 0)
    .map(({ label, count }) => `${label} (${count})`);

  return blockingDependencies.length > 0
    ? `This tenant cannot be deleted until dependencies are removed: ${blockingDependencies.join(', ')}.`
    : 'This tenant cannot be deleted because it still has dependencies.';
}

function getDeletionBlockers(tenant) {
  const dependencies = tenant?.dependencies || {};
  const dependencyLabels = {
    users: 'Active users',
    fortnoxTokens: 'Fortnox tokens',
    crmRows: 'CRM records',
    alerts: 'alerts',
    snapshots: 'snapshots',
  };

  return Object.entries(dependencyLabels)
    .map(([key, label]) => ({ key, label, count: Number(dependencies[key] || 0) }))
    .filter(({ count }) => count > 0);
}

export default function TenantSettingsPage({ actingTenant, onRefreshSession, setError }) {
  const navigate = useNavigate();
  const [tenants, setTenants] = useState([]);
  const [isLoadingList, setIsLoadingList] = useState(true);
  const [listError, setListError] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const deferredSearch = useDeferredValue(searchInput.trim().toLowerCase());
  const [selectedTenantId, setSelectedTenantId] = useState(null);
  const [selectedTenant, setSelectedTenant] = useState(null);
  const [isLoadingDetail, setIsLoadingDetail] = useState(false);
  const [detailError, setDetailError] = useState('');
  const [editorMode, setEditorMode] = useState('empty');
  const [createForm, setCreateForm] = useState(createDraft);
  const [editForm, setEditForm] = useState(createDraft);
  const [editorFeedback, setEditorFeedback] = useState(null);
  const [pageFeedback, setPageFeedback] = useState(null);
  const [isSaving, setIsSaving] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isConfirmingDelete, setIsConfirmingDelete] = useState(false);
  const activeTenantId = actingTenant?.id ?? null;

  const visibleTenants = useMemo(() => {
    if (!deferredSearch) {
      return tenants;
    }

    return tenants.filter((tenant) =>
      [tenant.company, tenant.clientId || ''].some((value) => value.toLowerCase().includes(deferredSearch))
    );
  }, [deferredSearch, tenants]);

  const replaceTenant = useCallback((nextTenant) => {
    setTenants((currentTenants) => orderTenants(
      currentTenants.some((tenant) => tenant.id === nextTenant.id)
        ? currentTenants.map((tenant) => (tenant.id === nextTenant.id ? nextTenant : tenant))
        : [...currentTenants, nextTenant]
    ));
  }, []);

  const redirectIfForbidden = useCallback((error) => {
    if (!isForbidden(error)) {
      return false;
    }

    setError(error.message || 'Forbidden');
    navigate('/', { replace: true });
    return true;
  }, [navigate, setError]);

  const loadTenants = useCallback(async () => {
    setIsLoadingList(true);
    setListError('');

    try {
      const nextTenants = await fetchTenants();
      setTenants(orderTenants(nextTenants));
    } catch (error) {
      if (!redirectIfForbidden(error)) {
        setListError(error.message || 'Failed to load tenants.');
      }
      setTenants([]);
    } finally {
      setIsLoadingList(false);
    }
  }, [redirectIfForbidden]);

  useEffect(() => {
    loadTenants();
  }, [loadTenants]);

  const handleSelectTenant = async (tenantId) => {
    setSelectedTenantId(tenantId);
    setEditorMode('edit');
    setIsLoadingDetail(true);
    setDetailError('');
    setEditorFeedback(null);
    setIsConfirmingDelete(false);

    try {
      const tenant = await fetchTenantById(tenantId);
      setSelectedTenant(tenant);
      setEditForm(editDraft(tenant));
      replaceTenant(tenant);
    } catch (error) {
      if (!redirectIfForbidden(error)) {
        setDetailError(error.message || 'Failed to load the tenant.');
      }
    } finally {
      setIsLoadingDetail(false);
    }
  };

  const handleStartCreate = () => {
    setSelectedTenantId(null);
    setSelectedTenant(null);
    setCreateForm(createDraft());
    setEditorFeedback(null);
    setDetailError('');
    setIsConfirmingDelete(false);
    setEditorMode('create');
  };

  const handleSubmitCreate = async (event) => {
    event.preventDefault();
    const company = createForm.company.trim();

    if (!company) {
      setEditorFeedback({ severity: 'error', message: 'Company is required.' });
      return;
    }

    setIsSaving(true);
    setEditorFeedback(null);

    try {
      const request = {
        company,
        clientId: createForm.clientId.trim(),
        crmEnabled: createForm.crmEnabled,
      };
      const clientSecret = createForm.clientSecret;
      if (clientSecret.trim()) {
        request.clientSecret = clientSecret;
      }

      const tenant = await createTenant(request);
      replaceTenant(tenant);
      setSelectedTenantId(tenant.id);
      setSelectedTenant(tenant);
      setCreateForm(createDraft());
      setEditForm(editDraft(tenant));
      setEditorMode('edit');
      setEditorFeedback({ severity: 'success', message: `Tenant ${tenant.company} created.` });
    } catch (error) {
      if (!redirectIfForbidden(error)) {
        setEditorFeedback({ severity: 'error', message: error.message || 'Failed to create the tenant.' });
      }
    } finally {
      setIsSaving(false);
    }
  };

  const handleSubmitEdit = async (event) => {
    event.preventDefault();
    const company = editForm.company.trim();

    if (!company) {
      setEditorFeedback({ severity: 'error', message: 'Company is required.' });
      return;
    }

    setIsSaving(true);
    setEditorFeedback(null);

    try {
      const request = {
        company,
        crmEnabled: editForm.crmEnabled,
      };
      const clientId = editForm.clientId.trim();
      const clientSecret = editForm.clientSecret;

      if (editForm.clearClientId) {
        request.clientId = '';
      } else if (clientId) {
        request.clientId = clientId;
      }

      if (clientSecret.trim()) {
        request.clientSecret = clientSecret;
      }

      const tenant = await updateTenant(selectedTenantId, request);
      replaceTenant(tenant);
      setSelectedTenant(tenant);
      setEditForm(editDraft(tenant));

      if (tenant.id === activeTenantId) {
        try {
          await onRefreshSession();
        } catch {
          setEditorFeedback({
            severity: 'warning',
            message: `Tenant ${tenant.company} updated, but the session could not be refreshed.`,
          });
          return;
        }
      }

      setEditorFeedback({ severity: 'success', message: `Tenant ${tenant.company} updated.` });
    } catch (error) {
      if (!redirectIfForbidden(error)) {
        setEditorFeedback({ severity: 'error', message: error.message || 'Failed to update the tenant.' });
      }
    } finally {
      setIsSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!selectedTenant?.deletable) {
      return;
    }

    setIsDeleting(true);
    setEditorFeedback(null);

    try {
      await deleteTenant(selectedTenant.id);
    } catch (error) {
      if (!redirectIfForbidden(error)) {
        setEditorFeedback({ severity: 'error', message: formatDeletionConflict(error) });
      }
      setIsConfirmingDelete(false);
      setIsDeleting(false);
      return;
    }

    const deletedTenant = selectedTenant;
    setTenants((currentTenants) => currentTenants.filter((tenant) => tenant.id !== deletedTenant.id));
    setSelectedTenantId(null);
    setSelectedTenant(null);
    setEditorMode('empty');
    setIsConfirmingDelete(false);
    setPageFeedback({ severity: 'success', message: `Tenant ${deletedTenant.company} deleted.` });

    if (deletedTenant.id === activeTenantId) {
      try {
        await onRefreshSession();
      } catch {
        setPageFeedback({
          severity: 'warning',
          message: `Tenant ${deletedTenant.company} deleted, but the session could not be refreshed.`,
        });
      }
    }

    setIsDeleting(false);
  };

  const renderListPanel = () => (
    <Paper elevation={0} sx={{ ...SURFACE_SX, p: { xs: 1.5, md: 1.75 }, height: '100%' }}>
      <Stack spacing={1.25}>
        <Stack direction="row" spacing={1} justifyContent="space-between" alignItems="center">
          <Box>
            <Typography variant="subtitle1" fontWeight={700}>
              Tenant records
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {tenants.length} tenants
            </Typography>
          </Box>
          <Button size="small" variant="contained" startIcon={<AddIcon />} onClick={handleStartCreate}>
            New
          </Button>
        </Stack>

        <TextField
          label="Search tenants"
          size="small"
          value={searchInput}
          onChange={(event) => setSearchInput(event.target.value)}
          fullWidth
        />

        {isLoadingList && <LinearProgress />}
        {listError && <Alert severity="error">{listError}</Alert>}

        {!isLoadingList && !listError && visibleTenants.length === 0 && (
          <Alert severity="info">
            {tenants.length === 0 ? 'No tenants have been created.' : 'No tenants match the search.'}
          </Alert>
        )}

        {!isLoadingList && !listError && visibleTenants.length > 0 && (
          <List disablePadding>
            {visibleTenants.map((tenant) => (
              <ListItemButton
                key={tenant.id}
                selected={selectedTenantId === tenant.id}
                onClick={() => handleSelectTenant(tenant.id)}
                sx={{ borderRadius: 2, mb: 0.5, alignItems: 'flex-start' }}
              >
                <ListItemText
                  primary={<Typography variant="body1" fontWeight={600}>{tenant.company}</Typography>}
                  secondaryTypographyProps={{ component: 'div' }}
                  secondary={
                    <Stack spacing={0.5} sx={{ mt: 0.5 }}>
                      <Typography variant="caption" color="text.secondary">
                        {tenant.clientId || 'No Fortnox client ID'} | {tenant.usage.users} users
                      </Typography>
                      <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap>
                        <Chip
                          size="small"
                          label={tenant.crmEnabled ? 'CRM enabled' : 'CRM disabled'}
                          color={tenant.crmEnabled ? 'success' : 'default'}
                          variant="outlined"
                        />
                        <Chip
                          size="small"
                          label={tenant.fortnoxConfigured ? 'Fortnox configured' : 'Fortnox incomplete'}
                          variant="outlined"
                        />
                      </Stack>
                    </Stack>
                  }
                />
              </ListItemButton>
            ))}
          </List>
        )}
      </Stack>
    </Paper>
  );

  const renderTenantStatus = (tenant) => (
    <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap>
      <Chip
        size="small"
        color={tenant.crmEnabled ? 'success' : 'default'}
        label={tenant.crmEnabled ? 'CRM enabled' : 'CRM disabled'}
      />
      <Chip
        size="small"
        color={tenant.hasClientSecret ? 'primary' : 'default'}
        variant="outlined"
        label={tenant.hasClientSecret ? 'Client secret configured' : 'Client secret not configured'}
      />
      <Chip
        size="small"
        color={tenant.fortnoxConfigured ? 'success' : 'default'}
        variant="outlined"
        label={tenant.fortnoxConfigured ? 'Fortnox configured' : 'Fortnox incomplete'}
      />
    </Stack>
  );

  const renderCreatePanel = () => (
    <Paper elevation={0} sx={{ ...SURFACE_SX, p: { xs: 2, md: 2.25 }, height: '100%' }}>
      <Stack component="form" spacing={2} onSubmit={handleSubmitCreate}>
        <Box>
          <Typography variant="h6" fontWeight={700}>Create tenant</Typography>
          <Typography variant="body2" color="text.secondary">
            Add a tenant record and configure its enabled module state.
          </Typography>
        </Box>

        {editorFeedback && <Alert severity={editorFeedback.severity}>{editorFeedback.message}</Alert>}

        <TextField
          label="Company"
          size="small"
          required
          value={createForm.company}
          onChange={(event) => setCreateForm({ ...createForm, company: event.target.value })}
        />
        <TextField
          label="Fortnox client ID"
          size="small"
          value={createForm.clientId}
          onChange={(event) => setCreateForm({ ...createForm, clientId: event.target.value })}
        />
        <TextField
          label="Fortnox client secret"
          type="password"
          autoComplete="new-password"
          size="small"
          value={createForm.clientSecret}
          onChange={(event) => setCreateForm({ ...createForm, clientSecret: event.target.value })}
        />
        <FormControlLabel
          control={
            <Switch
              checked={createForm.crmEnabled}
              onChange={(event) => setCreateForm({ ...createForm, crmEnabled: event.target.checked })}
            />
          }
          label="CRM enabled"
        />
        <Button
          type="submit"
          variant="contained"
          startIcon={<SaveIcon />}
          disabled={isSaving}
          sx={{ alignSelf: 'flex-start' }}
        >
          {isSaving ? 'Creating...' : 'Create tenant'}
        </Button>
      </Stack>
    </Paper>
  );

  const renderEditPanel = () => (
    <Paper elevation={0} sx={{ ...SURFACE_SX, p: { xs: 2, md: 2.25 }, height: '100%' }}>
      <Stack spacing={2}>
        {isLoadingDetail && <LinearProgress />}
        {detailError && <Alert severity="error">{detailError}</Alert>}
        {selectedTenant && (
          <>
            <Stack spacing={0.75}>
              <Typography variant="h6" fontWeight={700}>{selectedTenant.company}</Typography>
              {renderTenantStatus(selectedTenant)}
              <Typography variant="body2" color="text.secondary">
                {selectedTenant.usage.users} users | Updated {formatTimestamp(selectedTenant.updatedAt)}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Current Fortnox client ID: {selectedTenant.clientId || 'Not configured'}
              </Typography>
            </Stack>

            <Stack component="form" spacing={2} onSubmit={handleSubmitEdit}>
              {editorFeedback && <Alert severity={editorFeedback.severity}>{editorFeedback.message}</Alert>}
              <TextField
                label="Company"
                size="small"
                required
                value={editForm.company}
                onChange={(event) => setEditForm({ ...editForm, company: event.target.value })}
              />
              <TextField
                label="New Fortnox client ID"
                size="small"
                value={editForm.clientId}
                disabled={editForm.clearClientId}
                helperText="Leave blank to keep the current client ID."
                onChange={(event) => setEditForm({ ...editForm, clientId: event.target.value })}
              />
              <FormControlLabel
                control={
                  <Checkbox
                    checked={editForm.clearClientId}
                    onChange={(event) => setEditForm({
                      ...editForm,
                      clientId: event.target.checked ? '' : editForm.clientId,
                      clearClientId: event.target.checked,
                    })}
                  />
                }
                label="Clear current Fortnox client ID on save"
              />
              <TextField
                label="Fortnox client secret"
                type="password"
                autoComplete="new-password"
                size="small"
                value={editForm.clientSecret}
                helperText="Leave blank to keep the configured secret."
                onChange={(event) => setEditForm({ ...editForm, clientSecret: event.target.value })}
              />
              <FormControlLabel
                control={
                  <Switch
                    checked={editForm.crmEnabled}
                    onChange={(event) => setEditForm({ ...editForm, crmEnabled: event.target.checked })}
                  />
                }
                label="CRM enabled"
              />
              <Button
                type="submit"
                variant="contained"
                startIcon={<SaveIcon />}
                disabled={isSaving}
                sx={{ alignSelf: 'flex-start' }}
              >
                {isSaving ? 'Saving...' : 'Save changes'}
              </Button>
            </Stack>

            <Stack spacing={1} sx={{ pt: 1, borderTop: '1px solid rgba(0,0,0,0.08)' }}>
              <Typography variant="subtitle2" fontWeight={700}>Delete tenant</Typography>
              {!selectedTenant.deletable && (
                <Alert severity="info">
                  <Stack spacing={0.75}>
                    <Typography variant="body2">
                      This tenant cannot be deleted until these connected elements are removed:
                    </Typography>
                    <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap>
                      {getDeletionBlockers(selectedTenant).map(({ key, label, count }) => (
                        <Chip key={key} size="small" label={`${label}: ${count}`} variant="outlined" />
                      ))}
                    </Stack>
                  </Stack>
                </Alert>
              )}
              {isConfirmingDelete && (
                <Alert severity="warning">
                  Delete this tenant permanently? Inactive tenant users will be removed; business data is never deleted automatically.
                </Alert>
              )}
              <Stack direction="row" spacing={1}>
                {!isConfirmingDelete ? (
                  <Button
                    size="small"
                    variant="outlined"
                    color="error"
                    startIcon={<DeleteOutlineIcon />}
                    disabled={!selectedTenant.deletable || isSaving || isDeleting}
                    onClick={() => setIsConfirmingDelete(true)}
                  >
                    Delete tenant
                  </Button>
                ) : (
                  <>
                    <Button
                      size="small"
                      variant="contained"
                      color="error"
                      disabled={isDeleting}
                      onClick={handleDelete}
                    >
                      {isDeleting ? 'Deleting...' : 'Confirm delete'}
                    </Button>
                    <Button
                      size="small"
                      disabled={isDeleting}
                      onClick={() => setIsConfirmingDelete(false)}
                    >
                      Cancel
                    </Button>
                  </>
                )}
              </Stack>
            </Stack>
          </>
        )}
      </Stack>
    </Paper>
  );

  const renderPlaceholder = () => (
    <Paper elevation={0} sx={{ ...SURFACE_SX, p: { xs: 2, md: 2.25 }, height: '100%' }}>
      <Stack spacing={1.5} alignItems="flex-start">
        <Avatar sx={{ bgcolor: `${pageColors.tenantSettings}20`, color: pageColors.tenantSettings }}>
          <BusinessIcon />
        </Avatar>
        <Typography variant="h6" fontWeight={700}>Select a tenant or create a new one</Typography>
        <Typography variant="body2" color="text.secondary">
          Open a tenant record to review configuration and update supported settings.
        </Typography>
      </Stack>
    </Paper>
  );

  return (
    <Box sx={{ background: PAGE_BACKGROUND, minHeight: '100vh', py: { xs: 3, md: 4 }, px: { xs: 1, sm: 2 } }}>
      <Container maxWidth="xl">
        <Stack spacing={2.25}>
          <Paper elevation={0} sx={{ ...SURFACE_SX, p: { xs: 1.5, md: 2 } }}>
            <Stack direction="row" spacing={1.5} alignItems="center">
              <Avatar sx={{ width: 48, height: 48, bgcolor: pageColors.tenantSettings }}>
                <BusinessIcon />
              </Avatar>
              <Box>
                <Typography variant="overline" sx={{ color: pageColors.tenantSettings }}>
                  SUPER ADMIN
                </Typography>
                <Typography variant="h5" fontWeight={700}>Tenants</Typography>
                <Typography variant="body2" color="text.secondary">
                  Manage tenant records, Fortnox configuration status, and CRM enablement.
                </Typography>
              </Box>
            </Stack>
          </Paper>

          {pageFeedback && <Alert severity={pageFeedback.severity}>{pageFeedback.message}</Alert>}

          <Box sx={{ display: 'grid', gridTemplateColumns: TENANT_COLUMNS_SX, gap: 2, alignItems: 'stretch' }}>
            <Box sx={{ minWidth: 0 }}>{renderListPanel()}</Box>
            <Box sx={{ minWidth: 0 }}>
              {editorMode === 'create'
                ? renderCreatePanel()
                : editorMode === 'edit'
                  ? renderEditPanel()
                  : renderPlaceholder()}
            </Box>
          </Box>
        </Stack>
      </Container>
    </Box>
  );
}
